import Markdown from '@/features/_shared/Markdown';
// oxlint-disable-next-line gx-boundaries/no-public-api-internal-alias-import -- the migration plan requires path aliases for edited imports and this component consumes an ai-chat internal.
import ToolApprovalPrompt from '@/features/ai-chat/components/AskGXDrawer/ToolApprovalPrompt/ToolApprovalPrompt';
// oxlint-disable-next-line gx-boundaries/no-public-api-internal-alias-import -- the migration plan requires path aliases for edited imports and this component consumes an ai-chat internal.
import WidgetMapper from '@/features/ai-chat/components/WidgetMapper/WidgetMapper';
// oxlint-disable-next-line gx-boundaries/no-public-api-internal-alias-import -- the migration plan requires path aliases for edited imports and this component consumes an ai-chat internal.
import { linkifyGxCodes } from '@/features/ai-chat/lib/gxCodes';

import type { MessagePart, UIMessage } from '@tanstack/ai-client';

const getPartKey = (part: MessagePart, index: number): string => {
  if (part.type === 'text') return `text-${index}`;
  if (part.type === 'tool-call') return `tool-call-${part.id}-${index}`;
  return `content-part-${index}`;
};

const normalizeWidgetIdPart = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, '-');

function getToolTitle(input: unknown): string | undefined {
  if (!input || typeof input !== 'object' || !('title' in input)) return undefined;
  return typeof input.title === 'string' ? input.title : undefined;
}

// The backend calls one generic tool for every chart type, so a completed
// tool-call part's own `name` (fixed at TOOL_CALL_START, before the backend
// reveals the specific chart type) isn't a reliable widget-registry key.
// `tanstackConnection.ts`'s `toolOutputFromResult` resolves the real widget
// name from the result's `display_type` and carries it as `output.widgetTool`
// — prefer that, falling back to `name` for client tools (e.g.
// `add_to_dashboard`) that don't go through that resolution.
function resolveWidgetToolName(output: unknown, fallback: string): string {
  if (output && typeof output === 'object' && 'widgetTool' in output) {
    const value = (output as { widgetTool?: unknown }).widgetTool;
    if (typeof value === 'string') return value;
  }
  return fallback;
}

function withWidgetMetadata({
  output,
  toolName,
  toolCallId,
  messageId,
  partIndex
}: {
  output: unknown;
  toolName: string;
  toolCallId: string;
  messageId: string;
  partIndex: number;
}): unknown {
  if (!output || typeof output !== 'object' || 'error' in output) return output;

  return {
    ...output,
    tool_call_id: toolCallId,
    ...(toolName === 'render_static_chart'
      ? { widgetId: normalizeWidgetIdPart(`${messageId}-${toolCallId}-${partIndex}`) }
      : {})
  };
}

const AIContent = ({ msg }: { msg: UIMessage }) => (
  <>
    {msg.parts.map((part, index) => {
      if (part.type === 'text') {
        return <Markdown key={getPartKey(part, index)}>{linkifyGxCodes(part.content)}</Markdown>;
      }

      if (part.type !== 'tool-call') return null;

      if (part.state === 'approval-requested' && part.approval) {
        return (
          <ToolApprovalPrompt
            key={getPartKey(part, index)}
            interrupt={{
              toolCallId: part.id,
              toolName: part.name,
              approvalId: part.approval.id
            }}
            tab={{
              id: part.id,
              name: getToolTitle(part.input) ?? 'AI Chat',
              type: 'feed'
            }}
          />
        );
      }

      if (part.state === 'complete') {
        const toolName = resolveWidgetToolName(part.output, part.name);
        if (toolName === 'render_default') return null;

        return (
          <WidgetMapper
            key={getPartKey(part, index)}
            output={withWidgetMetadata({
              output: part.output,
              toolName,
              toolCallId: part.id,
              messageId: msg.id,
              partIndex: index
            })}
            toolName={toolName}
          />
        );
      }

      return null;
    })}
  </>
);

export default AIContent;
